import { Component, inject, input, OnInit, output, TemplateRef, viewChild } from '@angular/core';
import { BehaviorSubject, catchError, from, of, switchMap, tap } from 'rxjs';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DropdownComponent } from '../../../common/components/dropdown/dropdown.component';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { RoomService } from '../../../services/room/room.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { SearchbarComponent } from '../../../common/components/searchbar/searchbar.component';
import { GenericTableComponent, TableColumn } from '../../core/generic-table/generic-table.component';
import { SearchPublicRoomDialogTestIds } from './sb-search-public-room-dialog.test-ids';

interface RoomTableRow {
  name: string;
  members: number;
  canonicalAlias?: string;
  roomId: string;
  joined: boolean;
  button?: any;
}

@Component({
  selector: 'app-search-public-room-dialog',
  templateUrl: './sb-search-public-room-dialog.component.html',
  styleUrl: './sb-search-public-room-dialog.component.scss',
  imports: [ButtonComponent, MatIcon, FormsModule, AsyncPipe, MatDialogClose, SearchbarComponent, GenericTableComponent]
})
/**
 * Komponente für das Suchen und Beitreten zu öffentlichen Räumen.
 *
 * BEGRÜNDUNG: Es gibt keine Afo
 */
export class SbSearchPublicRoomDialogComponent implements OnInit {
  readonly actionTemplate = viewChild.required<TemplateRef<any>>('actionTemplate');
  readonly publicDropdownComponent = input<DropdownComponent>();
  readonly privateDropdownComponent = input<DropdownComponent>();
  readonly navigatedRoom = output<string>();
  readonly collapsed = input<boolean>();
  allRooms: RoomTableRow[] = [];
  rooms$ = new BehaviorSubject<RoomTableRow[]>([]);
  pageSize = 5;
  filterTerm = '';
  pageIndex = 0;
  itemsPerPage = 5;
  total = 0;
  displayedColumns: TableColumn<RoomTableRow>[] = [];
  columns: TableColumn<RoomTableRow>[] = [];
  protected readonly testIds = SearchPublicRoomDialogTestIds;
  protected readonly matDialog = inject(MatDialog);
  protected readonly clientService = inject(MatrixService);
  private readonly roomService = inject(RoomService);
  private readonly router = inject(Router);
  private readonly snackbarService = inject(SnackbarService);

  ngOnInit(): void {
    this.columns = [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'members', label: 'Mitglieder', sortable: false },
      { key: 'canonicalAlias', label: 'Alias', sortable: false },
      {
        key: 'button',
        label: 'Aktion',
        type: 'custom',
        template: this.actionTemplate()
      }
    ];
    this.displayedColumns = [...this.columns];
  }

  openDialog(template: TemplateRef<MatDialog>, $event?: Event): void {
    from(this.roomService.getPublicRoomInfos())
      .pipe(
        tap(infos => {
          this.allRooms = infos.map(info => ({
            name: info.room.name || 'Unbenannter Raum',
            members: info.memberCount,
            canonicalAlias: info.canonicalAlias ?? '',
            roomId: info.room.roomId,
            joined: info.joined
          }));

          this.applyFilter();
        }),
        catchError(err => {
          console.error(err);
          this.allRooms = [];
          this.applyFilter();
          return of(null);
        })
      )
      .subscribe();

    this.matDialog.closeAll();
    this.matDialog.open(template, {
      width: '53rem'
    });
    if ($event) $event.stopPropagation();
  }

  applyFilter(): void {
    const term = this.filterTerm.trim().toLowerCase();

    const filtered = this.allRooms.filter(
      row => row.name.toLowerCase().includes(term) || row.canonicalAlias?.toLowerCase().includes(term)
    );

    this.total = filtered.length;

    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    const pagedRows = filtered.slice(start, end);

    this.rooms$.next(pagedRows);
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyFilter();
  }

  onFilterChange(value: string): void {
    this.filterTerm = value;
    this.pageIndex = 0;
    this.applyFilter();
  }

  joinRoom(roomId: string): void {
    from(this.clientService.client.joinRoom(roomId))
      .pipe(
        switchMap(() => from(this.router.navigate(['rooms', roomId]))),
        tap(navigated => {
          if (navigated) {
            this.navigatedRoom.emit(roomId);
            this.privateDropdownComponent()?.closePanel();
            this.publicDropdownComponent()?.openPanel();
          }
          this.matDialog.closeAll();
        }),
        catchError(err => {
          console.error(err);
          this.snackbarService.error('Fehler beim Beitritt zum Raum.');
          return of(null);
        })
      )
      .subscribe();
  }

  reset(): void {
    this.pageSize = 5;
    this.filterTerm = '';
    this.pageIndex = 0;
    this.itemsPerPage = 5;
  }

  viewRoom(roomId: string): void {
    from(this.router.navigate(['rooms', roomId]))
      .pipe(
        tap(navigated => {
          if (navigated) {
            this.navigatedRoom.emit(roomId);
            this.privateDropdownComponent()?.closePanel();
            this.publicDropdownComponent()?.openPanel();
          }
          this.matDialog.closeAll();
        }),
        catchError(err => {
          console.error(err);
          this.snackbarService.error('Fehler beim Anzeigen des Raums.');
          return of(null);
        })
      )
      .subscribe();
  }
}
